import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  IconButton,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonSearch as PersonSearchIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  INTL_REGION_LIST,
  INTL_REGIONS,
  isIntlOrder,
  intlCountryOf,
  type IntlCountryCode,
} from '../config/intlRegions';

type DeliveryMethod = 'pickup' | 'shipping' | 'intl';
type PaymentMode = 'no_charge' | 'prepaid' | 'cod';

interface ManualOrderItem {
  itemId: string | number | null;     // storefront_products.shopify_id
  variantId: string | number | null;  // storefront_product_variants.shopify_variant_id
  title: string;
  variantDetails: string;
  sku: string;
  price: number;
  quantity: number;
  imageUrl: string;
}

interface SearchVariant {
  id: string | number;
  title: string;
  price: string;
  sku: string;
  available: boolean;
  option1?: string | null;
  option2?: string | null;
  option3?: string | null;
}

interface SearchProduct {
  id: string | number;
  handle: string;
  title: string;
  product_type: string;
  image_src: string;
  variants: SearchVariant[];
  price_range: { min: string; max: string };
}

interface OriginalOrderItem {
  itemId: string | number | null;
  variantId: string | number | null;
  title: string;
  sku: string;
  price: number;       // original listed unit price at order time
  paidPrice?: number;  // actually-paid per unit (after order-level discounts/credits)
  quantity: number;
  imageUrl: string;
  variantDetails: string;
  productHandle: string | null;
  itemType: string | null;
  availableVariants: SearchVariant[];
}

const LOGISTICS_OPTIONS: { value: string; label: string }[] = [
  { value: 'UNIMARTC2C', label: '7-ELEVEN 賣貨便' },
  { value: 'FAMIC2C', label: '全家 店到店' },
  { value: 'HILIFEC2C', label: '萊爾富 店到店' },
  { value: 'OKMARTC2C', label: 'OK 超商 店到店' },
];

// Identity used to merge duplicate lines. Prefer the storefront variant id when
// present; outlet (and other variant-less) items have variantId === null, so
// fall back to their own item id + sku + variant so distinct outlet pieces don't
// collapse into one line.
const lineKey = (it: { variantId: string | number | null; itemId: string | number | null; sku: string; variantDetails: string }) =>
  it.variantId != null
    ? `v:${it.variantId}`
    : `i:${it.itemId ?? ''}|${it.sku || ''}|${it.variantDetails || ''}`;

// All variants of a product share one SKU (workspace rule), so the first
// non-empty variant sku labels the whole product in the picker.
const skuOf = (p: SearchProduct) => p.variants.find(v => v.sku)?.sku || '';

const todayIsoDate = () => {
  const now = new Date();
  const tz = now.getTimezoneOffset();
  return new Date(now.getTime() - tz * 60_000).toISOString().slice(0, 10);
};

const ManualStorefrontOrderPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Customer / shipping state
  const [lookupOrderId, setLookupOrderId] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>('pickup');
  const [logisticsSubType, setLogisticsSubType] = useState<string>('UNIMARTC2C');
  const [storeId, setStoreId] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingCountry, setShippingCountry] = useState<IntlCountryCode>('HK');
  const [lookupLoading, setLookupLoading] = useState(false);

  // Items
  const [items, setItems] = useState<ManualOrderItem[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchProduct[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Original-order items returned from lookup, used as quick-add candidates.
  const [originalOrderId, setOriginalOrderId] = useState<string | null>(null);
  const [originalItems, setOriginalItems] = useState<OriginalOrderItem[]>([]);
  // Map each original item index → currently selected variant id (so user can switch size/color).
  const [variantPick, setVariantPick] = useState<Record<number, string>>({});

  // Order params
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('no_charge');
  const [totalAmount, setTotalAmount] = useState<string>('0');
  const [orderDate, setOrderDate] = useState<string>(todayIsoDate());
  const [note, setNote] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const itemsSubtotal = useMemo(
    () => items.reduce((s, it) => s + it.price * it.quantity, 0),
    [items]
  );

  // Switch payment mode — auto-fill amount from subtotal when moving to a charge mode.
  const handlePaymentModeChange = (next: PaymentMode) => {
    setPaymentMode(next);
    if (next === 'no_charge') {
      setTotalAmount('0');
    } else if (totalAmount === '0' || totalAmount === '') {
      setTotalAmount(String(itemsSubtotal));
    }
  };

  // --- Customer lookup ----------------------------------------------------
  const handleLookup = useCallback(async () => {
    const oid = lookupOrderId.trim();
    if (!oid && !phone.trim() && !email.trim()) {
      enqueueSnackbar('輸入訂單編號、電話或 Email 才能查詢', { variant: 'warning' });
      return;
    }
    setLookupLoading(true);
    try {
      const params: Record<string, string> = {};
      if (oid) params.orderId = oid;
      if (phone.trim()) params.phone = phone.trim();
      if (email.trim()) params.email = email.trim();
      const { data } = await api.get('/api/storefront/orders/customer-lookup', { params });
      if (!data.found) {
        const msg = oid
          ? `找不到訂單 ${oid}，請確認是否為 storefront 訂單`
          : '找不到此客戶的歷史訂單，請手動填入資料';
        enqueueSnackbar(msg, { variant: 'info' });
        return;
      }
      const o = data.order;
      setName(o.customer_name || '');
      setPhone(o.customer_phone || phone);
      setEmail(o.customer_email || email);
      // International (SF HK/MO) orders come back as delivery_method 'intl'
      // (or legacy 'intl_hk'); map those to the intl option and pre-select the
      // destination country so the resend keeps shipping via SF.
      let dm: DeliveryMethod;
      if (isIntlOrder(o)) {
        dm = 'intl';
        setShippingCountry(intlCountryOf(o) || 'HK');
      } else {
        dm = o.delivery_method === 'shipping' ? 'shipping' : 'pickup';
      }
      setDeliveryMethod(dm);
      if (dm === 'pickup') {
        setLogisticsSubType(o.logistics_sub_type || 'UNIMARTC2C');
        setStoreId(o.store_id || '');
        setStoreName(o.store_name || '');
        setStoreAddress(o.store_address || '');
      } else {
        setShippingAddress(o.shipping_address || '');
      }
      // Store original items so admin can quick-add or switch their variants.
      const fetchedItems: OriginalOrderItem[] = Array.isArray(data.items) ? data.items : [];
      const isLegacy = data.source === 'shopify';
      const refLabel = isLegacy
        ? `Shopify #${o.legacy_order_number || o.order_id}`
        : o.order_id;
      setOriginalOrderId(refLabel);
      setOriginalItems(fetchedItems);
      // Default each picker to the variant that was actually shipped originally.
      const defaultPicks: Record<number, string> = {};
      fetchedItems.forEach((it, idx) => {
        defaultPicks[idx] = it.variantId != null ? String(it.variantId) : '';
      });
      setVariantPick(defaultPicks);

      // Auto-fill the note with original order ref if user hasn't typed one.
      setNote(prev => prev.trim() ? prev : `原訂單 ${refLabel}`);

      const sourceMsg = isLegacy ? ' (舊 Shopify 訂單)' : '';
      enqueueSnackbar(`已帶入訂單 ${refLabel} 的資料${sourceMsg}`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(`查詢失敗: ${err?.response?.data?.error || err.message}`, { variant: 'error' });
    } finally {
      setLookupLoading(false);
    }
  }, [lookupOrderId, phone, email, enqueueSnackbar]);

  // --- Quick-add an item from the original order (with variant pick) ------
  const addFromOriginal = (idx: number) => {
    const orig = originalItems[idx];
    if (!orig) return;
    const pickedId = variantPick[idx] || (orig.variantId != null ? String(orig.variantId) : '');
    const variant = orig.availableVariants.find(v => String(v.id) === String(pickedId));
    const variantDetails = variant
      ? [variant.option1, variant.option2, variant.option3].filter(Boolean).join(' / ')
      : orig.variantDetails || '';
    const sku = variant?.sku || orig.sku || '';
    // When user keeps the original variant, default to the actually-paid unit
    // price (post-discount). When they swap to a different size/color, fall
    // back to that variant's current listed price.
    const keptOriginal = String(pickedId) === String(orig.variantId);
    const paid = typeof orig.paidPrice === 'number' ? orig.paidPrice : orig.price;
    const price = keptOriginal
      ? (paid || 0)
      : (variant ? (parseFloat(variant.price) || 0) : (paid || 0));

    const newItem: ManualOrderItem = {
      itemId: orig.itemId,
      variantId: variant ? variant.id : orig.variantId,
      title: orig.title,
      variantDetails,
      sku,
      price,
      quantity: 1,
      imageUrl: orig.imageUrl,
    };
    setItems(prev => {
      const existing = prev.findIndex(p => lineKey(p) === lineKey(newItem));
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], quantity: next[existing].quantity + 1 };
        return next;
      }
      return [...prev, newItem];
    });
  };

  // --- Product picker -----------------------------------------------------
  const runSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchLoading(true);
    try {
      const { data } = await api.get('/api/storefront/outfit-looks/admin/search-products', {
        params: { q },
      });
      setSearchResults(data.products || []);
    } catch (err: any) {
      enqueueSnackbar(`搜尋失敗: ${err?.response?.data?.error || err.message}`, { variant: 'error' });
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, enqueueSnackbar]);

  const addVariant = (product: SearchProduct, variant: SearchVariant) => {
    const variantDetails = [variant.option1, variant.option2, variant.option3]
      .filter(Boolean)
      .join(' / ');
    const candidate: ManualOrderItem = {
      itemId: product.id,
      variantId: variant.id,
      title: product.title,
      variantDetails,
      sku: variant.sku || '',
      price: parseFloat(variant.price) || 0,
      quantity: 1,
      imageUrl: product.image_src || '',
    };
    setItems(prev => {
      const existing = prev.findIndex(p => lineKey(p) === lineKey(candidate));
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], quantity: next[existing].quantity + 1 };
        return next;
      }
      return [...prev, candidate];
    });
  };

  const updateItem = (idx: number, patch: Partial<ManualOrderItem>) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // --- Submit -------------------------------------------------------------
  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('請填入收件人姓名', { variant: 'warning' });
      return;
    }
    if (!phone.trim()) {
      enqueueSnackbar('請填入電話', { variant: 'warning' });
      return;
    }
    if (items.length === 0) {
      enqueueSnackbar('至少要加一個商品', { variant: 'warning' });
      return;
    }
    if (deliveryMethod === 'pickup' && !storeId.trim()) {
      enqueueSnackbar('超商取貨需要 store_id', { variant: 'warning' });
      return;
    }
    if (deliveryMethod === 'shipping' && !shippingAddress.trim()) {
      enqueueSnackbar('宅配需要收件地址', { variant: 'warning' });
      return;
    }
    if (deliveryMethod === 'intl' && !shippingAddress.trim()) {
      enqueueSnackbar('國際配送需要收件地址', { variant: 'warning' });
      return;
    }
    if (paymentMode === 'cod' && deliveryMethod !== 'pickup') {
      enqueueSnackbar('貨到付款只能搭配超商取貨', { variant: 'warning' });
      return;
    }
    if (paymentMode !== 'no_charge' && (Number(totalAmount) || 0) <= 0) {
      enqueueSnackbar('請輸入收款金額', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        customerName: name.trim(),
        customerPhone: phone.trim(),
        customerEmail: email.trim() || null,
        deliveryMethod,
        logisticsSubType: deliveryMethod === 'pickup' ? logisticsSubType : null,
        storeId: deliveryMethod === 'pickup' ? storeId.trim() : null,
        storeName: deliveryMethod === 'pickup' ? storeName.trim() : null,
        storeAddress: deliveryMethod === 'pickup' ? storeAddress.trim() : null,
        shippingAddress: deliveryMethod === 'pickup' ? null : shippingAddress.trim(),
        shippingCountry: deliveryMethod === 'intl' ? shippingCountry : 'TW',
        items: items.map(it => ({
          itemId: it.itemId,
          variantId: it.variantId,
          title: it.title,
          price: it.price,
          quantity: it.quantity,
          sku: it.sku,
          imageUrl: it.imageUrl,
          variantDetails: it.variantDetails,
        })),
        paymentMode,
        totalAmount: paymentMode === 'no_charge' ? 0 : Number(totalAmount) || 0,
        orderDate,
        note: note.trim() || null,
      };
      const { data } = await api.post('/api/storefront/orders/manual', payload);
      enqueueSnackbar(`已建立補寄訂單 ${data.orderId}`, { variant: 'success' });
      navigate('/storefront-orders');
    } catch (err: any) {
      enqueueSnackbar(`建立失敗: ${err?.response?.data?.error || err.message}`, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        手動建立補寄訂單
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        用於 送錯/補寄。訂單建立時直接標記為 PAID，並寫入 storefront orders。
        可選擇是否向客戶收款及金額，並可指定訂單日期。
      </Alert>

      <Stack spacing={2}>
        {/* --- Customer card --- */}
        <Card>
          <CardHeader
            title="客戶資料"
            subheader="可用訂單編號、電話或 Email 查詢並帶入上次訂單資料 (訂單編號優先)"
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="原訂單編號 (例如 ORD-1234)"
                  value={lookupOrderId}
                  onChange={e => setLookupOrderId(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="電話"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Email (選填)"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleLookup(); }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={lookupLoading ? <CircularProgress size={16} /> : <PersonSearchIcon />}
                  onClick={handleLookup}
                  disabled={lookupLoading}
                  sx={{ height: '40px' }}
                >
                  查詢並帶入訂單資料
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="收件人姓名"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  size="small"
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* --- Delivery card --- */}
        <Card>
          <CardHeader title="配送方式" />
          <CardContent>
            <FormControl>
              <RadioGroup
                row
                value={deliveryMethod}
                onChange={e => {
                  const next = e.target.value as DeliveryMethod;
                  setDeliveryMethod(next);
                  if (next !== 'pickup' && paymentMode === 'cod') {
                    handlePaymentModeChange('prepaid');
                  }
                }}
              >
                <FormControlLabel value="pickup" control={<Radio />} label="超商取貨" />
                <FormControlLabel value="shipping" control={<Radio />} label="宅配" />
                <FormControlLabel value="intl" control={<Radio />} label="國際配送（順豐／Parxl）" />
              </RadioGroup>
            </FormControl>

            {deliveryMethod === 'pickup' ? (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>超商通路</InputLabel>
                    <Select
                      label="超商通路"
                      value={logisticsSubType}
                      onChange={e => setLogisticsSubType(e.target.value)}
                    >
                      {LOGISTICS_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="門市代號 (store_id)"
                    value={storeId}
                    onChange={e => setStoreId(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="門市名稱"
                    value={storeName}
                    onChange={e => setStoreName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="門市地址"
                    value={storeAddress}
                    onChange={e => setStoreAddress(e.target.value)}
                  />
                </Grid>
              </Grid>
            ) : deliveryMethod === 'intl' ? (
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>配送國家/地區</InputLabel>
                    <Select
                      label="配送國家/地區"
                      value={shippingCountry}
                      onChange={e => setShippingCountry(e.target.value as IntlCountryCode)}
                    >
                      {INTL_REGION_LIST.map(r => (
                        <MenuItem key={r.code} value={r.code}>
                          {r.flag} {r.nameZh} ({r.nameEn})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label={`收件地址（英文，${INTL_REGIONS[shippingCountry].carrierLabel}面單用）`}
                    value={shippingAddress}
                    onChange={e => setShippingAddress(e.target.value)}
                    required
                    multiline
                    minRows={2}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity={INTL_REGIONS[shippingCountry].carrier === 'sf' ? 'info' : 'warning'}>
                    此訂單會以 <strong>國際（intl / {shippingCountry}）</strong> 建立，
                    配送業者為 <strong>{INTL_REGIONS[shippingCountry].carrierLabel}</strong>。
                    {INTL_REGIONS[shippingCountry].carrier === 'sf'
                      ? `建立後於出貨頁點「${INTL_REGIONS[shippingCountry].nameZh}出貨」產生順豐面單（填寫收件人地區/證件）。`
                      : `建立後於出貨頁點「${INTL_REGIONS[shippingCountry].nameZh}出貨」建立 Parxl 託運單並列印面單（需填實際包裹重量，運費依重量級距計費）。地址結尾請含 6 位數郵遞區號。`}
                    貨到付款不適用國際配送。
                  </Alert>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ mt: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="收件地址"
                  value={shippingAddress}
                  onChange={e => setShippingAddress(e.target.value)}
                  required
                  multiline
                  minRows={2}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* --- Original-order items quick-add panel --- */}
        {originalItems.length > 0 && (
          <Card>
            <CardHeader
              title={`從原訂單加入商品 (${originalOrderId})`}
              subheader="可以直接加入原本訂單的商品，或切換尺寸/顏色"
            />
            <CardContent>
              <Stack divider={<Divider />} spacing={1.5}>
                {originalItems.map((it, idx) => {
                  const variants = it.availableVariants || [];
                  const pickedId = variantPick[idx] ?? (it.variantId != null ? String(it.variantId) : '');
                  return (
                    <Box key={`orig-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {it.imageUrl ? (
                        <img src={it.imageUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                      ) : (
                        <Box sx={{ width: 56, height: 56, bgcolor: 'grey.200', borderRadius: 1 }} />
                      )}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>{it.title}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          原本: {it.variantDetails || '—'}{it.sku ? ` · SKU ${it.sku}` : ''} · 數量 {it.quantity}
                        </Typography>
                        {typeof it.paidPrice === 'number' && it.paidPrice !== it.price ? (
                          <Typography variant="caption" color="success.main" display="block">
                            原訂單實付 NT${it.paidPrice.toLocaleString()}/件
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.5, textDecoration: 'line-through' }}
                            >
                              NT${it.price.toLocaleString()}
                            </Typography>
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" display="block">
                            原訂單單價 NT${(it.paidPrice ?? it.price).toLocaleString()}/件
                          </Typography>
                        )}
                      </Box>
                      {variants.length > 0 ? (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>變體</InputLabel>
                          <Select
                            label="變體"
                            value={pickedId}
                            onChange={e => setVariantPick(prev => ({ ...prev, [idx]: e.target.value as string }))}
                          >
                            {variants.map(v => {
                              const label = [v.option1, v.option2, v.option3].filter(Boolean).join(' / ') || v.title || v.sku;
                              return (
                                <MenuItem key={String(v.id)} value={String(v.id)}>
                                  {label}{v.available ? '' : ' (缺)'} · NT${v.price}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 200 }}>
                          無對應 storefront 商品
                        </Typography>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => addFromOriginal(idx)}
                        disabled={variants.length === 0 && it.itemType !== 'storefront' && it.itemType !== 'outlet'}
                      >
                        加入
                      </Button>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* --- Items card --- */}
        <Card>
          <CardHeader
            title="補寄商品"
            action={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => { setPickerOpen(true); setSearchResults([]); setSearchQuery(''); }}
              >
                搜尋其他商品
              </Button>
            }
          />
          <CardContent>
            {items.length === 0 ? (
              <Typography color="text.secondary">尚未加入商品</Typography>
            ) : (
              <Stack divider={<Divider />} spacing={1.5}>
                {items.map((it, idx) => (
                  <Box key={`${it.variantId}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {it.imageUrl ? (
                      <img src={it.imageUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />
                    ) : (
                      <Box sx={{ width: 56, height: 56, bgcolor: 'grey.200', borderRadius: 1 }} />
                    )}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>{it.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {it.variantDetails}{it.sku ? ` · SKU ${it.sku}` : ''}
                      </Typography>
                    </Box>
                    <TextField
                      label="單價"
                      size="small"
                      type="number"
                      sx={{ width: 110 }}
                      value={it.price}
                      onChange={e => updateItem(idx, { price: Math.max(0, Number(e.target.value) || 0) })}
                      InputProps={{ startAdornment: <InputAdornment position="start">NT$</InputAdornment> }}
                    />
                    <TextField
                      label="數量"
                      size="small"
                      type="number"
                      sx={{ width: 80 }}
                      value={it.quantity}
                      onChange={e => updateItem(idx, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                    />
                    <IconButton onClick={() => removeItem(idx)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                  <Typography variant="body1">
                    商品小計：<strong>NT${itemsSubtotal.toLocaleString()}</strong>
                  </Typography>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* --- Charge / date / note --- */}
        <Card>
          <CardHeader title="付款與訂單日期" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl>
                  <FormLabel>付款方式</FormLabel>
                  <RadioGroup
                    value={paymentMode}
                    onChange={e => handlePaymentModeChange(e.target.value as PaymentMode)}
                  >
                    <FormControlLabel
                      value="no_charge"
                      control={<Radio />}
                      label="不向客戶收費 (NT$0, 已付款)"
                    />
                    <FormControlLabel
                      value="prepaid"
                      control={<Radio />}
                      label="客戶已用其他方式付款 (LINE Pay/轉帳, 訂單記為已付款)"
                    />
                    <FormControlLabel
                      value="cod"
                      control={<Radio />}
                      label="貨到付款 (超商取貨時收款)"
                      disabled={deliveryMethod !== 'pickup'}
                    />
                  </RadioGroup>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="收款金額"
                  value={paymentMode === 'no_charge' ? '0' : totalAmount}
                  onChange={e => setTotalAmount(e.target.value)}
                  disabled={paymentMode === 'no_charge'}
                  sx={{ mt: 1 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">NT$</InputAdornment> }}
                  helperText={
                    paymentMode === 'no_charge'
                      ? '訂單會以 PAID + NT$0 建立'
                      : paymentMode === 'cod'
                      ? '訂單會以 ORDERED + store_cod 建立, 客戶取貨時付款'
                      : '訂單會以 PAID + manual_resend 建立, 表示已收款'
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormLabel>訂單日期</FormLabel>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={orderDate}
                  onChange={e => setOrderDate(e.target.value)}
                  sx={{ mt: 1 }}
                />
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label="今天"
                    onClick={() => setOrderDate(todayIsoDate())}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="備註 (選填) — 例如：原訂單 ORD-1234 送錯尺寸"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => navigate('/storefront-orders')} disabled={submitting}>
            取消
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={submitting}
          >
            建立補寄訂單
          </Button>
        </Box>
      </Stack>

      {/* --- Product picker dialog --- */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>選擇商品</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="搜尋商品名稱、handle 或 SKU（例：3312）"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
            />
            <Button
              variant="contained"
              startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
              onClick={runSearch}
              disabled={searchLoading || !searchQuery.trim()}
            >
              搜尋
            </Button>
          </Box>

          {searchResults.length === 0 && !searchLoading ? (
            <Typography color="text.secondary">輸入關鍵字或 SKU 後按搜尋</Typography>
          ) : (
            <Stack spacing={1.5} divider={<Divider />}>
              {searchResults.map(p => (
                <Box key={String(p.id)} sx={{ display: 'flex', gap: 2 }}>
                  {p.image_src ? (
                    <img src={p.image_src} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4 }} />
                  ) : (
                    <Box sx={{ width: 64, height: 64, bgcolor: 'grey.200', borderRadius: 1 }} />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight={500}>{p.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[skuOf(p), p.product_type].filter(Boolean).join(' · ')}
                    </Typography>
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {p.variants.map(v => {
                        const label = [v.option1, v.option2, v.option3].filter(Boolean).join(' / ') || v.title || v.sku;
                        return (
                          <Chip
                            key={String(v.id)}
                            size="small"
                            label={`${label} · NT$${v.price}${v.available ? '' : ' (缺)'}`}
                            color={v.available ? 'primary' : 'default'}
                            variant={v.available ? 'filled' : 'outlined'}
                            onClick={() => addVariant(p, v)}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>完成</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualStorefrontOrderPage;
